import './styles/ventingArchives.scss';

import { Listing, Pagination } from '../components/Primitives';
import React, { useEffect, useMemo, useState } from 'react';

import ApplianceAccordion from '../components/appliance/ApplianceAccordion';
import { Breadcrumb } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Primitives';
import { IAppliance } from '../interfaces';
import Layout from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Layout/Layout';
import { ListingFilter } from '../components/Listing';
import PageTitle from '../components/Primitives/PageTitle';
import SEO from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Seo';
import arrowDown from '../images/englander-icons/Down-Arrow-Icon.svg';
import { graphql } from 'gatsby';
import { popLastIndex } from '../utils';
import useMobileDetect from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Hooks/useMobileDetect';
import { usePageLocalization } from '@sbi-international/boutik.gatsby.plugin.multilocale';
import { useProductsPricesAndCode } from '@progymedia/gatsby-theme-boutik-ecommerce/src/context/store/queries/useProductsPriceAndInventories';
import { useQueryArg } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Hooks/useQueryArg';
import { useTranslation } from 'react-i18next';

export const query = graphql`
    query ($category: String!, $cmsPage: String!) {
        listing: allProductsJson(filter: { category: { regex: $category } }) {
            nodes {
                code
                id
                documents {
                    en_CA
                    en_US
                    fr_CA
                }
                description {
                    en_CA
                    en_US
                    fr_CA
                    valuesMarkdownRemark {
                        en_CA
                        en_US
                        fr_CA
                    }
                }
                title {
                    en_CA
                    en_US
                    fr_CA
                }
                slug {
                    en_CA
                    en_US
                    fr_CA
                }
                category
                category_localized {
                    en_CA
                    en_US
                    fr_CA
                }
                fields {
                    ...productsFieldsImagesFragment
                }
                attributes {
                    custom_made {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    diameter {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                }
            }
        }
        listingCms: allProductsJson(filter: { code: { eq: $cmsPage } }) {
            nodes {
                ...cmsDataFragment
                attributes {
                    meta_title {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    meta_description {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                }
            }
        }
    }
`;

export default function VentingArchives({ data, location, pageContext }) {
    const allProductsRaw = data.listing.nodes;
    const priceAndCode = [
        ...useProductsPricesAndCode(
            allProductsRaw?.map((family: any) => family.code)
        ),
    ];
    const allProducts = allProductsRaw.map((prod) => {
        const pPrice = priceAndCode?.[0]?.find((e) => e.sku === prod.code);
        return {
            ...prod,
            ...pPrice,
        };
    });
    const filteredVenting = allProducts.filter(
        (e) =>
            !pageContext.parentCategory ||
            e.category === pageContext.parentCategory
    );
    const { t } = useTranslation('url');
    const { currentLocale } = usePageLocalization();
    const [filteredProducts, setFilteredProducts] = useState(filteredVenting);
    const isMobile = useMobileDetect().isMobile();
    const [page, setPage] = useState(1);
    const APPLIANCE_PER_PAGE = isMobile ? 6 : 12;
    const allCategoryMap = getVentingCategoryMap(
        data.listing.nodes,
        currentLocale
    );
    const [diameterArg] = useQueryArg('diameter');
    const [filterDiameterState, setFilterDiameterState] = useState(
        diameterArg ? diameterArg : ''
    );
    const [pageCategory, setPageCategory] = useState(
        pageContext?.parentCategory
    );
    const listingCms = data?.listingCms?.nodes?.[0];
    const defaultBaseProd = filteredVenting?.[0];
    const pageTitle = listingCms?.title?.[currentLocale] || '';
    const pageDesc = listingCms?.description?.[currentLocale] || '';

    const metadataAttributes = listingCms?.attributes;
    const metadata = {
        title: metadataAttributes?.meta_title?.values[0][currentLocale],
        description:
            metadataAttributes?.meta_description?.values[0][currentLocale],
    };

    const ventingType =
        defaultBaseProd?.category_localized?.[currentLocale]
            ?.split('/')
            .at(-3) || '';
    /*   const pageTitle = !pageCategory
        ? ventingType
        : defaultBaseProd?.category_localized?.[currentLocale]
              ?.split('/')
              .at(-2) || data?.listingCms?.nodes?.[0]?.title?.[currentLocale]; */
    const [showSmallFilter, setShowSmallFilter] = useState(true);
    const [displayMobileFilter, setDisplayMobileFilter] = useState(true);
    const [sort, setSort] = useQueryArg('sort');
    const prodPriceCp = [
        ...useProductsPricesAndCode(
            allProducts.map((family: { code: string }) => family.code)
        ),
    ];

    if (location && typeof window !== 'undefined')
        window.scrollTo(0, location?.state?.scrollPosition);

    useEffect(() => {
        if (diameterArg) {
            setFilterDiameterState(diameterArg);
        } else {
            setFilterDiameterState('');
        }
        setPage(1);
    }, [diameterArg]);

    return (
        <Layout>
            <SEO title={metadata?.title} description={metadata?.description} />
            <Breadcrumb
                className='venting-listing-breadcrumb'
                elements={[
                    { link: '/', name: t('layout:appliance.home') },
                    {
                        link: `/venting`,
                        name: t('layout:venting.title'),
                    },
                    { link: '', name: pageTitle },
                ]}
            />
            <div className='venting-listing-page'>
                {!isMobile && (
                    <VentingFilter
                        allCategoryMap={allCategoryMap}
                        setFilteredProducts={setFilteredProducts}
                        allProducts={allProducts}
                        ventingType={ventingType}
                        filterDiameterState={filterDiameterState}
                        diameterArg={diameterArg}
                        selected={defaultBaseProd?.slug?.[currentLocale]}
                        pageCategory={pageCategory}
                        setPageCategory={setPageCategory}
                        defaultProduct={defaultBaseProd}
                    />
                )}
                <div className='venting-listing-container'>
                    <PageTitle title={pageTitle} description={pageDesc}>
                        <ListingFilter
                            filteredProducts={filteredProducts}
                            setFilteredProducts={setFilteredProducts}
                            setPage={setPage}
                            priceAndCode={prodPriceCp}
                            setSort={setSort}
                            sort={sort}
                            showSmallFilter={showSmallFilter}
                            setShowSmallFilter={setShowSmallFilter}
                            displayMobileFilter={displayMobileFilter}
                            setDisplayMobileFilter={setDisplayMobileFilter}
                        />
                    </PageTitle>
                    {isMobile && displayMobileFilter && (
                        <VentingFilter
                            allCategoryMap={allCategoryMap}
                            setFilteredProducts={setFilteredProducts}
                            allProducts={allProducts}
                            ventingType={ventingType}
                            filterDiameterState={filterDiameterState}
                            diameterArg={diameterArg}
                            selected={pageTitle}
                            pageCategory={pageCategory}
                            setPageCategory={setPageCategory}
                            defaultProduct={defaultBaseProd}
                        />
                    )}
                    <Listing
                        items={
                            filteredProducts &&
                            filteredProducts.length > 0 &&
                            filteredProducts?.slice(
                                APPLIANCE_PER_PAGE * (page - 1),
                                APPLIANCE_PER_PAGE * page
                            )
                        }
                        displayLinkOverride
                        displayManual={false}
                        displayDescription={true}
                        hasOwnPageOverride
                    />
                </div>
            </div>
            {filteredProducts &&
                filteredProducts.length / APPLIANCE_PER_PAGE > 1 && (
                    <Pagination
                        currentPage={page}
                        onPageChange={(newPage: number) => setPage(newPage)}
                        pageNumber={Math.ceil(
                            filteredProducts.length / APPLIANCE_PER_PAGE
                        )}
                        showOnePage={isMobile}
                    />
                )}
        </Layout>
    );
}
function VentingFilter(props: {
    allCategoryMap: Map<any, any>;
    setFilteredProducts: React.Dispatch<any>;
    allProducts: any[];
    ventingType: string;
    filterDiameterState: string;
    diameterArg: string;
    selected: string;
    pageCategory: string;
    setPageCategory: React.Dispatch<string>;
    defaultProduct: any;
}) {
    const { t } = useTranslation('layout');
    const { currentLocale, navigateTranslated } = usePageLocalization();
    const {
        allCategoryMap,
        allProducts,
        setFilteredProducts,
        ventingType,
        filterDiameterState,
        diameterArg,
        selected,
        pageCategory,
        setPageCategory,
        defaultProduct,
    } = props;

    const [accordionState] = useState([true, true]);
    useEffect(() => {
        let prod = allProducts;
        prod = prod.filter((e) => e.category === pageCategory || !pageCategory);
        prod = prod.filter((e) => {
            if (diameterArg?.length === 0) return true;
            return diameterArg.includes(
                // On compare avec le premier chiffre seulement pour ignorer le \" que Studio retourne
                e?.attributes?.diameter?.values?.[0]?.['fr_CA']?.[0]
            );
        });
        setFilteredProducts(prod);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        currentLocale,
        setFilteredProducts,
        ventingType,
        filterDiameterState,
        pageCategory,
    ]);

    let ventingSlug = defaultProduct?.slug?.[currentLocale] || '';
    if (ventingSlug.startsWith('/englander')) {
        ventingSlug = ventingSlug.replace('/englander', '');
    }
    const slugTranslated =
        ventingSlug == ''
            ? ''
            : popLastIndex({ text: ventingSlug, padEnd: true });
    const diameters: string[] = useMemo(() => {
        if (ventingSlug?.includes(t('url:flex-liner'))) {
            return ['4', '5', '6'];
        } else if (
            ventingSlug?.includes(t('url:double-wall-stove-pipe')) ||
            ventingSlug?.includes(t('url:single-wall-stove-pipe'))
        ) {
            return ['6', '7', '8'];
        } else if (ventingSlug?.includes(t('url:pellet-vent'))) {
            return ['3', '4'];
        } else {
            return [];
        }
    }, [t, ventingSlug]);

    return (
        <div className='venting-listing-filter'>
            <ApplianceAccordion title={ventingType} open={accordionState[0]}>
                {RenderCategoryFilter(
                    allCategoryMap,
                    ventingType,
                    navigateTranslated,
                    selected,
                    setPageCategory,
                    pageCategory
                )}
            </ApplianceAccordion>
            {diameters?.length > 0 && (
                <ApplianceAccordion
                    title={t('venting.diameter')}
                    open={accordionState[1]}
                >
                    {RenderDiameterFilter(
                        navigateTranslated,
                        ventingType,
                        diameters,
                        diameterArg,
                        slugTranslated
                    )}
                </ApplianceAccordion>
            )}
        </div>
    );
}

function RenderCategoryFilter(
    allCategoryMap: Map<string, any>,
    ventingType: string,
    navigateTranslated: (url: string, scrollPosition?: number) => void,
    selected: string,
    setPageCategory: React.Dispatch<string>,
    pageCategory: string
): any {
    const returnArray: any[] = [];
    if (ventingType) {
        for (const [key, value] of allCategoryMap) {
            const currentType = key.toLowerCase();
            if (ventingType.toLocaleLowerCase() === currentType) {
                returnArray.push(
                    <div className='venting-filter-category' key={key}>
                        <div className='filter-checkbox-group'>
                            {value.map((single, index) => {
                                return (
                                    <SingleCheckBox
                                        key={single.label}
                                        preId={'venting-filter-categories-'}
                                        index={index}
                                        single={single}
                                        isCheckedByDefault={
                                            selected.includes(single.slug) &&
                                            !!pageCategory
                                        }
                                        onChange={(checked) => {
                                            const scrollPosition =
                                                document.documentElement
                                                    .scrollTop ||
                                                document.body.scrollTop;
                                            if (checked) {
                                                navigateTranslated(
                                                    `/venting/${single.slug}/`,
                                                    scrollPosition
                                                );
                                            } else {
                                                navigateTranslated(
                                                    `/venting/${popLastIndex({
                                                        text: single.slug,
                                                    })}/`,
                                                    scrollPosition
                                                );
                                            }
                                        }}
                                    />
                                );
                            })}
                        </div>
                    </div>
                );
            }
        }
    }
    return returnArray;
}
function RenderDiameterFilter(
    navigateTranslated: (url: string, scrollPosition?: number) => void,
    ventingType: string,
    diameters: string[],
    diameterArg,
    urlTranslated: string
): any {
    return (
        <div className='venting-filter-category'>
            <div className='filter-checkbox-group'>
                {diameters?.map((diameter, index) => {
                    return (
                        <SingleCheckBox
                            key={`diameter-${diameter}`}
                            preId={`venting-filter-diameter-${diameter}`}
                            index={index}
                            single={{ label: `${diameter}"` }}
                            isCheckedByDefault={diameterArg.includes(diameter)}
                            onChange={(checked) => {
                                const scrollPosition =
                                    document.documentElement.scrollTop ||
                                    document.body.scrollTop;

                                if (checked) {
                                    const oldDiameter = `${
                                        diameterArg ? diameterArg + ',' : ''
                                    }`;

                                    navigateTranslated(
                                        `${urlTranslated}?diameter=${
                                            oldDiameter + diameter
                                        }`,
                                        scrollPosition
                                    );
                                } else {
                                    let oldDiameter = `${diameterArg
                                        ?.split(',')
                                        ?.filter((diam) => diam !== diameter)
                                        ?.join(',')}`;

                                    oldDiameter =
                                        oldDiameter.length > 0
                                            ? `?diameter=${oldDiameter}`
                                            : '';

                                    navigateTranslated(
                                        `${urlTranslated}${oldDiameter}${
                                            oldDiameter.length > 0 ? '&' : '?'
                                        }`,
                                        scrollPosition
                                    );
                                }
                            }}
                        />
                    );
                })}
            </div>
        </div>
    );
}

function SingleCheckBox(props: {
    index: number;
    single: any;
    onChange: (checked: boolean) => void;
    preId: string;
    isCheckedByDefault?: boolean;
}) {
    const { index, single, onChange, preId, isCheckedByDefault } = props;
    const [isChecked, setChecked] = useState(false);
    useEffect(() => {
        if (isCheckedByDefault) {
            setChecked(true);
        }
    }, [isCheckedByDefault]);
    return (
        <div className='checkbox-and-label' key={index}>
            <input
                id={`${preId}${index}`}
                type='checkbox'
                onChange={(event) => {
                    if (onChange) {
                        onChange(event.target.checked);
                        setChecked(event.target.checked);
                    }
                }}
                checked={isChecked}
            />
            <label htmlFor={`${preId}${index}`} className='checkbox-label'>
                {single.label}
            </label>
        </div>
    );
}

function getVentingCategoryMap(products: any, currentLocale: string) {
    const categoryMap = new Map();
    products.forEach((product) => {
        const re = /-/gi;
        const categoryLocalized: string[] =
            product.category_localized[currentLocale].split('/');
        const ventingCategory = categoryLocalized[3].replace(re, ' ');
        const ventingSubCategoty = categoryLocalized[4];
        let slugTransform: string = product.slug[currentLocale];
        if (slugTransform.startsWith('/')) {
            slugTransform = slugTransform.slice(1);
        }
        const slug: string[] = slugTransform.split('/');
        const slugValue = slug[2] + '/' + slug[3];
        const collection = categoryMap.get(ventingCategory);
        if (!collection) {
            categoryMap.set(ventingCategory, [
                { label: ventingSubCategoty, slug: slugValue },
            ]);
        } else {
            if (!collection.find((e) => e.label === ventingSubCategoty)) {
                collection.push({ label: ventingSubCategoty, slug: slugValue });
                collection.sort((a, b) => a?.label?.localeCompare(b?.label));
            }
        }
    });
    return categoryMap;
}

function SortingFilter(props: {
    filteredProducts: any[];
    setFilteredProducts: React.Dispatch<React.SetStateAction<any[]>>;
    setPage?: React.Dispatch<React.SetStateAction<number>>;
}) {
    const { filteredProducts, setFilteredProducts, setPage } = props;
    const { t } = useTranslation('url');
    const [showSorter, setShowSorter] = useState(false);
    const { currentLocale } = usePageLocalization();

    // [Name, Diameter, Price Low, Price High]
    const [sorterState, setSorterState] = useState([
        false,
        false,
        false,
        false,
    ]);

    useEffect(() => {
        // [Name, Diameter, Price Low, Price High]
        if (sorterState[0]) {
            const sortedProducts = filteredProducts
                .slice()
                .sort(
                    (a: IAppliance, b: IAppliance) =>
                        a?.title?.[currentLocale].localeCompare(
                            b?.title?.[currentLocale]
                        )
                );
            setFilteredProducts(sortedProducts);
        } else if (sorterState[1]) {
            const sortedProducts = filteredProducts
                .slice()
                .sort(
                    (a: IAppliance, b: IAppliance) =>
                        parseInt(
                            a?.attributes?.diameter?.values?.[0]?.['en_CA'] ||
                                '0'
                        ) -
                        parseInt(
                            b?.attributes?.diameter?.values?.[0]?.['en_CA'] ||
                                '0'
                        )
                );
            setFilteredProducts(sortedProducts);
        } else if (sorterState[2]) {
            const sortedProducts = filteredProducts
                .slice()
                .sort((a: any, b: any) => {
                    if (a.price?.finalPrice?.value < b.price?.finalPrice?.value)
                        return -1;
                    if (a.price?.finalPrice?.value > b.price?.finalPrice?.value)
                        return 1;
                    return 0;
                });
            setFilteredProducts(sortedProducts);
        } else if (sorterState[3]) {
            const sortedProducts = filteredProducts
                .slice()
                .sort((a: any, b: any) => {
                    if (a.price?.finalPrice?.value > b.price?.finalPrice?.value)
                        return -1;
                    if (a.price?.finalPrice?.value < b.price?.finalPrice?.value)
                        return 1;
                    return 0;
                });
            setFilteredProducts(sortedProducts);
        }

        if (setPage) setPage(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentLocale, sorterState]);
    // TODO figure out sorting options
    return (
        <div className='sorting-filter'>
            <div
                className='filter-title'
                onClick={() => setShowSorter(!showSorter)}
            >
                {t('layout:review.filter.sort-by').replace(':', '')}
                <span
                    className='icon'
                    style={showSorter ? { transform: 'rotate(-180deg)' } : {}}
                >
                    <img src={arrowDown} alt='' />
                </span>
            </div>
            {showSorter && (
                <div className='content'>
                    <SortingCheckbox
                        label={t('layout:appliance.filter.name')}
                        index={0}
                        setSorterState={setSorterState}
                        sorterState={sorterState}
                        setShowSorter={setShowSorter}
                    />
                    <SortingCheckbox
                        label={t('layout:venting.diameter')}
                        index={1}
                        setSorterState={setSorterState}
                        sorterState={sorterState}
                        setShowSorter={setShowSorter}
                    />
                    <SortingCheckbox
                        label={t('layout:appliance.filter.price-low')}
                        index={2}
                        setSorterState={setSorterState}
                        sorterState={sorterState}
                        setShowSorter={setShowSorter}
                    />
                    <SortingCheckbox
                        label={t('layout:appliance.filter.price-high')}
                        index={3}
                        setSorterState={setSorterState}
                        sorterState={sorterState}
                        setShowSorter={setShowSorter}
                    />
                    {/* <SortingCheckbox
                        label={t('layout:appliance.filter.popularity')}
                        index={4}
                        setSorterState={setSorterState}
                        sorterState={sorterState}
                        setShowSorter={setShowSorter}
            /> */}
                </div>
            )}
        </div>
    );
}

function SortingCheckbox(props: {
    label: string;
    onChange?: (checked: boolean) => void;
    index: number;
    sorterState: boolean[];
    setSorterState: React.Dispatch<React.SetStateAction<boolean[]>>;
    setShowSorter: React.Dispatch<React.SetStateAction<boolean>>;
}) {
    const {
        onChange,
        label,
        index,
        setSorterState,
        sorterState,
        setShowSorter,
    } = props;
    return (
        <div className='checkbox-and-label'>
            <input
                id={`${label}`}
                type='checkbox'
                onChange={(event) => {
                    if (onChange) {
                        onChange(event.target.checked);
                    }
                    const tmpState = [false, false, false, false, false];
                    if (event.target.checked) {
                        tmpState[index] = true;
                        setSorterState(tmpState);
                    } else {
                        setSorterState(tmpState);
                    }
                    setShowSorter(false);
                }}
                checked={sorterState[index]}
            />
            <label htmlFor={`${label}`} className='checkbox-label'>
                {label}
            </label>
        </div>
    );
}

function filterByPrice(prices: any, filterState: string, index: number) {
    if (!filterState || filterState === '') {
        return true;
    }
    const valueArr = filterState.split('-');
    let max: any = null;
    let min: any = null;
    valueArr.forEach((e) => {
        try {
            if (!isNaN(parseInt(e))) {
                const number = parseInt(e);
                if (min === null || number <= min) {
                    min = number;
                }
                if (max === null || number >= max) {
                    max = number;
                }
            }
        } catch (e) {
            console.log('error catched: VentingArchives');
            console.log(e);
        }
    });
    const regularPrice: number = prices[0][index]?.regularPrice?.value
        ? prices[0][index]?.regularPrice?.value
        : 0;
    if (
        min === null ||
        max === null ||
        (regularPrice <= max && regularPrice >= min)
    ) {
        return true;
    }
    return false;
}
